import { Component } from '@theme/component';

const UGC_SECTION_SELECTOR = '.section-carousel--doggie-ugc';
const UGC_VIDEO_SELECTOR = 'video-background-component';
const UGC_TOGGLE_CLASS = 'ugc-video-sound-toggle';
const UGC_SOUND_ON_LABEL = 'Unmute';
const UGC_SOUND_OFF_LABEL = 'Mute';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  static ugcObserver = null;
  static ugcSections = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.autoplay = true;
    videoElement.loop = true;
    videoElement.playsInline = true;

    this.setupUgcSoundToggle();
    const playPromise = videoElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* no-op: browser autoplay policy may delay playback until user gesture */
      });
    }
  }

  disconnectedCallback() {
    const ugcSection = this.closest(UGC_SECTION_SELECTOR);
    if (ugcSection) {
      const state = VideoBackgroundComponent.ugcSections.get(ugcSection);
      state?.components.delete(this);
      if (state && state.components.size === 0) {
        VideoBackgroundComponent.ugcSections.delete(ugcSection);
        VideoBackgroundComponent.ugcObserver?.unobserve(ugcSection);
      }
    }
    super.disconnectedCallback();
  }

  setupUgcSoundToggle() {
    const ugcSection = this.closest(UGC_SECTION_SELECTOR);
    if (!ugcSection) return;

    this.classList.add('has-sound-toggle');

    const toggleButton = document.createElement('button');
    toggleButton.type = 'button';
    toggleButton.className = UGC_TOGGLE_CLASS;
    toggleButton.dataset.state = 'muted';
    toggleButton.addEventListener('click', () => {
      const nextMuted = !this.refs.videoElement.muted;
      VideoBackgroundComponent.applyUgcAudioState(ugcSection, this, nextMuted);
    });
    this.append(toggleButton);
    this.soundToggle = toggleButton;
    this.updateUgcToggleUi(true);

    let sectionState = VideoBackgroundComponent.ugcSections.get(ugcSection);
    if (!sectionState) {
      sectionState = {
        components: new Set(),
        activated: false,
      };
      VideoBackgroundComponent.ugcSections.set(ugcSection, sectionState);
    }
    sectionState.components.add(this);

    if (!VideoBackgroundComponent.ugcObserver) {
      VideoBackgroundComponent.ugcObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const state = VideoBackgroundComponent.ugcSections.get(entry.target);
            if (!state) return;
            if (entry.isIntersecting) {
              VideoBackgroundComponent.activateUgcSection(entry.target, state);
            } else {
              VideoBackgroundComponent.muteUgcSection(state);
            }
          });
        },
        {
          threshold: 0.35,
        }
      );
    }

    VideoBackgroundComponent.ugcObserver.observe(ugcSection);
  }

  updateUgcToggleUi(isMuted) {
    if (!this.soundToggle) return;
    this.soundToggle.dataset.state = isMuted ? 'muted' : 'unmuted';
    this.soundToggle.setAttribute('aria-pressed', String(!isMuted));
    this.soundToggle.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    this.soundToggle.textContent = isMuted ? UGC_SOUND_ON_LABEL : UGC_SOUND_OFF_LABEL;
  }

  static activateUgcSection(section, state) {
    if (!state.components.size) return;
    if (!state.activated) {
      const firstComponent = section.querySelector(UGC_VIDEO_SELECTOR);
      state.activated = true;
      state.components.forEach((component) => {
        const shouldMute = component !== firstComponent;
        component.refs.videoElement.muted = shouldMute;
        component.refs.videoElement.defaultMuted = shouldMute;
        component.updateUgcToggleUi(shouldMute);
        const playPromise = component.refs.videoElement.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
      return;
    }

    state.components.forEach((component) => {
      const playPromise = component.refs.videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }

  static muteUgcSection(state) {
    state.components.forEach((component) => {
      component.refs.videoElement.muted = true;
      component.refs.videoElement.defaultMuted = true;
      component.updateUgcToggleUi(true);
    });
    state.activated = false;
  }

  static applyUgcAudioState(section, selectedComponent, shouldMuteSelected) {
    const state = VideoBackgroundComponent.ugcSections.get(section);
    if (!state) return;

    state.activated = true;
    state.components.forEach((component) => {
      const isSelected = component === selectedComponent;
      const nextMuted = isSelected ? shouldMuteSelected : true;
      component.refs.videoElement.muted = nextMuted;
      component.refs.videoElement.defaultMuted = nextMuted;
      component.updateUgcToggleUi(nextMuted);
      const playPromise = component.refs.videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
